import { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Icon, Link, tokens } from '@hotjar/hotsauce';
import { getMinutesAndSeconds } from '@hotjar/insights-common/lib/getMinutesAndSeconds';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import { onSidebarEventClick } from '@hotjar/recordings/src/features/Playbacks/utils';
import { useStore } from '@hotjar/recordings/src/store/player/context';
import { EventBoxTranslationOptions, EventMarker } from '@hotjar/webapp/features/Playbacks/types';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { getIconName, getTargetUrl } from '@hotjar/webapp/features/Playbacks/EventMarkerUtils';
import { getEventBoxTitle } from '../../utils/getEventBoxTitle';
import { getEventBoxDescription } from '../../utils/getEventBoxDescription';

const highlightedStyle = css`
  background-color: ${tokens.color.ui_background};
  color: ${tokens.color.text_default};

  ${tokens.elevation(1)}
`;

const ContainerDiv = styled.button<{ isHighlighted: boolean; borderLeftColor?: string }>`
  background: transparent;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0 20px 12px;
  width: calc(100% - 40px);
  height: 40px;
  cursor: pointer;
  border: none;
  border-radius: 2px;
  border-left-style: solid;
  border-left-color: ${({ borderLeftColor }) => borderLeftColor ?? tokens.color.neutral_100};
  border-left-width: ${({ borderLeftColor }) => (borderLeftColor ? 2 : 0)}px;

  ${tokens.text('caption')}
  font-size: 10px;
  color: ${tokens.color.text_light};

  transition: all 0.2s ease-out;
  ${({ isHighlighted }) => isHighlighted && highlightedStyle};

  &:focus-visible {
    ${tokens.focus()};
    border-color: transparent;
    // We need the z-index to ensure the focus ring appears above tab siblings.
    z-index: 1;
  }
`;

const IconSpan = styled.span`
  flex-basis: 35px;
  flex-shrink: 0;
  flex-grow: 0;
`;

const ContentSpan = styled.span`
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
`;

const TimestampSpan = styled.span`
  flex-basis: 30px;
  flex-shrink: 0;
  padding: 0 5px;
`;

const TitleSpan = styled.span`
  font-weight: bold;
`;

const StyledLink = styled(Link)<{ isHighlighted: boolean }>`
  color: ${({ isHighlighted }) =>
    isHighlighted ? tokens.color.neutral_800 : tokens.color.neutral_500};
  text-decoration: none;
`;

const BORDER_COLORS = [
  tokens.color.magenta_600,
  tokens.color.ember_600,
  tokens.color.neutral_500,
  tokens.color.mint_600,
  tokens.color.mint_800,
];

const getOptionalBorderLeftColor = (eventMarker: EventMarker): string | undefined => {
  switch (eventMarker.type) {
    case 'key_press':
    case 'select_change':
    case 'input_choice_change':
      return '#333745';
    case 'viewport_resize':
      return tokens.BRAND_COLORS.neutral_700;
    case 'mouse_click':
      return tokens.color.neutral_100;
    case 'tag_recording':
      return '#5dffd3';
    case 'autotag_recording': {
      const behaviorName = eventMarker.subtype ?? eventMarker.name;
      switch (behaviorName) {
        case 'feedback': {
          if (eventMarker.autotag_properties?.e == null) {
            return undefined;
          }
          return BORDER_COLORS[+(eventMarker.autotag_properties?.e ?? 0)];
        }
        case 'poll': {
          if (eventMarker.autotag_properties?.e == null) {
            return tokens.color.lilac;
          }
          return BORDER_COLORS[+(eventMarker.autotag_properties?.e ?? 0)];
        }
        case 'rageclick':
          return '#FFD166';
        case 'uturn':
          return '#FFFF8D';
        default:
          return undefined;
      }
    }
    default:
      return undefined;
  }
};

const getIconColor = (eventMarker: EventMarker): string => {
  const behaviorName = eventMarker.subtype ? eventMarker.subtype : eventMarker.name;
  switch (behaviorName) {
    case 'poll':
      return tokens.color.lilac;
    default:
      return tokens.color.neutral_400;
  }
};

const StyledEmotionIcon = styled(EmotionIcon)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface Props {
  eventMarker: EventMarker;
  isDesktopRecording: boolean;
  recordingSiteId: number;
}

export const EventBoxElement = forwardRef<HTMLButtonElement, Props>(
  ({ eventMarker, isDesktopRecording, recordingSiteId }, ref) => {
    const playerStore = useStore();
    const isHighlighted = useRealTimeStore(
      useCallback(
        (state) => eventMarker.time <= state.currentTimeWithLessPrecision,
        [eventMarker.time],
      ),
    );
    const { tabCount } = useRealTimeStore.getState();
    const translationOptions: EventBoxTranslationOptions = {
      ...(eventMarker?.tab_index !== undefined && {
        page: {
          tabNumber: eventMarker.tab_index + 1,
          totalTab: tabCount,
        },
      }),
    };
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();

    const optionalBorderLeftColor = getOptionalBorderLeftColor(eventMarker);

    const onSideBarClick = useCallback(
      () => onSidebarEventClick(playerStore, eventMarker),
      [playerStore, eventMarker],
    );

    if (eventMarker.type === 'autotag_recording') {
      // User behavior

      const behaviorName = eventMarker.subtype ?? eventMarker.name;
      const targetUrl = getTargetUrl(recordingSiteId, eventMarker);
      const hasScore =
        (behaviorName === 'poll' || behaviorName === 'feedback') &&
        eventMarker.autotag_properties?.e != null;
      return (
        <ContainerDiv
          onClick={onSideBarClick}
          ref={ref}
          isHighlighted={isHighlighted || isHovered}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          borderLeftColor={optionalBorderLeftColor}
        >
          <IconSpan>
            {hasScore ? (
              <StyledEmotionIcon
                emotionStyle={behaviorName === 'feedback' ? 'default' : 'smiley'}
                score={(eventMarker.autotag_properties?.e as EmotionScore) ?? 0}
                size="s"
              />
            ) : (
              <Icon
                icon={getIconName(eventMarker, isDesktopRecording)}
                css={{ color: getIconColor(eventMarker) }}
              />
            )}
          </IconSpan>
          <ContentSpan>
            <TitleSpan>{getEventBoxTitle(eventMarker, t, translationOptions)}:</TitleSpan>&nbsp;
            {targetUrl ? (
              <StyledLink
                kind="inline"
                href={targetUrl}
                isExternal
                isHighlighted={isHighlighted || isHovered}
              >
                {getEventBoxDescription(eventMarker, t)}
              </StyledLink>
            ) : (
              <span>{getEventBoxDescription(eventMarker, t)}</span>
            )}
          </ContentSpan>
          <TimestampSpan>{getMinutesAndSeconds(eventMarker.time)}</TimestampSpan>
        </ContainerDiv>
      );
    }

    return (
      <ContainerDiv
        onClick={onSideBarClick}
        ref={ref}
        isHighlighted={isHighlighted || isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        borderLeftColor={optionalBorderLeftColor}
        aria-label={`Select action: ${getEventBoxTitle(
          eventMarker,
          t,
          translationOptions,
        )} at ${getMinutesAndSeconds(eventMarker.time)}`}
      >
        <IconSpan>
          <Icon
            icon={getIconName(eventMarker, isDesktopRecording)}
            css={{ color: getIconColor(eventMarker) }}
          />
        </IconSpan>
        <ContentSpan>
          <TitleSpan>{getEventBoxTitle(eventMarker, t, translationOptions)}:</TitleSpan>&nbsp;
          <span>{getEventBoxDescription(eventMarker, t)}</span>
        </ContentSpan>
        <TimestampSpan>{getMinutesAndSeconds(eventMarker.time)}</TimestampSpan>
      </ContainerDiv>
    );
  },
);

EventBoxElement.displayName = 'EventBoxElement';
